import type Phaser from 'phaser';
import type { CinematicState } from '../cinematic/cinematic-controller';
import type { SuperCharacterType } from './super-presentation-model';
import { SUPER_THEMES } from './super-presentation-model';

export class SuperActivationOverlay {
  public readonly container: Phaser.GameObjects.Container;
  private readonly dimGraphics: Phaser.GameObjects.Graphics;
  private readonly bannerGraphics: Phaser.GameObjects.Graphics;
  private readonly superLabel: Phaser.GameObjects.Text;
  private readonly tagLabel: Phaser.GameObjects.Text;
  private readonly nameLabel: Phaser.GameObjects.Text;
  private currentCharacterId: SuperCharacterType | null = null;

  public constructor(scene: Phaser.Scene) {
    this.container = scene.add.container(0, 0).setDepth(120).setVisible(false);

    // Fullscreen Screen Dim (30% alpha black)
    this.dimGraphics = scene.add.graphics();
    this.dimGraphics.fillStyle(0x000000, 0.32);
    this.dimGraphics.fillRect(0, 0, 1280, 720);

    // Banner graphics (Card and motifs)
    this.bannerGraphics = scene.add.graphics();

    // SUPER label
    this.superLabel = scene.add.text(640, 260, 'SUPER', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#facc15',
      align: 'center',
    }).setOrigin(0.5);

    // Subtitle / Tag text
    this.tagLabel = scene.add.text(640, 284, '', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#94a3b8',
      align: 'center',
    }).setOrigin(0.5);

    // Formal Super Name
    this.nameLabel = scene.add.text(640, 328, '', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '38px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#0f172a',
      strokeThickness: 6,
      align: 'center',
    }).setOrigin(0.5);

    this.container.add([
      this.dimGraphics,
      this.bannerGraphics,
      this.superLabel,
      this.tagLabel,
      this.nameLabel,
    ]);
  }

  public update(state: CinematicState | null, characterId: SuperCharacterType): void {
    if (state === null) {
      this.dismiss();
      return;
    }

    this.container.setVisible(true);
    this.currentCharacterId = characterId;
    const theme = SUPER_THEMES[characterId];

    // Compute progress & alpha based on stage
    let alpha = 1;
    let scale = 1;

    if (state.stage === 'EMPHASIS') {
      const p = Math.min(1, state.frame / 11);
      alpha = p;
      scale = 0.88 + 0.12 * p;
    } else if (state.stage === 'CUT_IN') {
      alpha = 1;
      scale = 1;
    } else if (state.stage === 'RESTORE') {
      const p = Math.min(1, (state.frame - 60) / 29);
      alpha = Math.max(0, 1 - p);
      scale = 1 + 0.04 * p;
    }

    this.container.setAlpha(alpha);

    // Update labels
    this.superLabel.setColor(theme.tagColor);
    this.tagLabel.setText(theme.tagText).setColor(theme.tagColor);
    this.nameLabel.setText(theme.superName).setColor(theme.textColor);

    if (characterId === 'sales') {
      this.nameLabel.setFontSize('44px');
      this.nameLabel.setStroke('#18181b', 6);
    } else {
      this.nameLabel.setFontSize('34px');
      this.nameLabel.setStroke('#090d16', 5);
    }

    // Redraw Banner and character-specific motifs
    this.renderBanner(characterId, theme, scale);
  }

  public dismiss(): void {
    this.container.setVisible(false);
    this.bannerGraphics.clear();
    this.currentCharacterId = null;
  }

  public get isVisible(): boolean {
    return this.container.visible;
  }

  private renderBanner(characterId: SuperCharacterType, theme: (typeof SUPER_THEMES)[SuperCharacterType], scale: number): void {
    const g = this.bannerGraphics;
    g.clear();

    const bannerW = 680 * scale;
    const bannerH = 140 * scale;
    const cx = 640;
    const cy = 310;
    const x = cx - bannerW / 2;
    const y = cy - bannerH / 2;

    // Card background
    g.fillStyle(theme.bannerBgColor, 0.94);
    g.fillRoundedRect(x, y, bannerW, bannerH, 6);

    // Accent line at top and bottom
    g.fillStyle(theme.primaryColor, 0.95);
    g.fillRect(x + 12, y + 2, bannerW - 24, 3);
    g.fillRect(x + 12, y + bannerH - 5, bannerW - 24, 3);

    // Border
    g.lineStyle(2, theme.bannerBorderColor, 0.9);
    g.strokeRoundedRect(x, y, bannerW, bannerH, 6);

    // Character-specific visual motifs
    if (characterId === 'sales') {
      // Sales Motifs: Forward Arrow lines & Stamp box
      g.lineStyle(1.5, 0xfde047, 0.6);
      g.lineBetween(x + 36, cy + 48, cx - 20, cy + 48);
      g.lineBetween(cx + 20, cy + 48, x + bannerW - 36, cy + 48);

      // Forward arrow tip
      g.fillStyle(0xfde047, 0.8);
      g.fillTriangle(x + bannerW - 36, cy + 48, x + bannerW - 46, cy + 42, x + bannerW - 46, cy + 54);

      // Gold Document Card Fragment accents (small tilted rects at corners)
      g.fillStyle(0xfacc15, 0.35);
      g.fillRect(x + 24, y + 16, 18, 14);
      g.fillRect(x + bannerW - 42, y + 16, 18, 14);
    } else {
      // Developer Motifs: Terminal Frame, Scanline dashes, Rollback indicator
      g.lineStyle(1.5, 0x38bdf8, 0.6);
      g.lineBetween(x + 36, cy + 48, cx - 20, cy + 48);
      g.lineBetween(cx + 20, cy + 48, x + bannerW - 36, cy + 48);

      // Terminal cursor indicator at left
      g.fillStyle(0x38bdf8, 0.8);
      g.fillRect(x + 28, cy - 8, 4, 18);

      // Compact Rollback Motifs (small arc indicators)
      g.lineStyle(1.5, 0xf472b6, 0.5);
      g.strokeCircle(x + bannerW - 34, cy - 2, 8);
      g.strokeRect(x + 24, y + 14, 16, 12);
      g.strokeRect(x + bannerW - 40, y + 14, 16, 12);
    }
  }
}
